import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigateWithParams } from '../hooks/useNavigateWithParams';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search,
  ArrowUpDown,
  Calendar,
  Archive,
  ArchiveRestore,
  Trash2,
  Eye,
  Plus,
  FolderOpen,
  AlertTriangle,
  X,
  Loader2,
  Link2,
  Share2,
  Link2Off,
  Rocket,
  Infinity,
  Globe,
  Activity,
  Pause,
} from 'lucide-react';
import {
  getWebinars,
  deleteWebinar,
  archiveWebinar,
  unarchiveWebinar,
  shareWebinar,
  revokeShare,
  makeEvergreen,
  removeEvergreen,
  type WebinarListItem,
} from '../lib/webinarHistory';

type DateFilter = '7d' | '30d' | '90d' | 'all';

export function WebinarHistory() {
  const navigate = useNavigateWithParams();
  const [webinars, setWebinars] = useState<WebinarListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [sortAsc, setSortAsc] = useState(false);
  const [dateFilter, setDateFilter] = useState<DateFilter>('all');
  const [showArchived, setShowArchived] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<WebinarListItem | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<WebinarListItem | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [linkCopied, setLinkCopied] = useState<string | null>(null);
  const [removeEvergreenTarget, setRemoveEvergreenTarget] = useState<WebinarListItem | null>(null);
  const [evergreenCopied, setEvergreenCopied] = useState<string | null>(null);

  const fetchWebinars = useCallback(async () => {
    try {
      setError(null);
      const data = await getWebinars({
        includeArchived: showArchived,
        sortAsc,
        dateFilter,
      });
      setWebinars(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load webinars');
    } finally {
      setLoading(false);
    }
  }, [showArchived, sortAsc, dateFilter]);

  useEffect(() => {
    setLoading(true);
    fetchWebinars();
  }, [fetchWebinars]);

  const filtered = useMemo(() => {
    if (!search.trim()) return webinars;
    const q = search.toLowerCase();
    return webinars.filter(
      (w) =>
        (w.title && w.title.toLowerCase().includes(q)) ||
        (w.subtitle && w.subtitle.toLowerCase().includes(q))
    );
  }, [webinars, search]);

  const archivedCount = useMemo(
    () => webinars.filter((w) => w.is_archived).length,
    [webinars]
  );

  const handleView = (id: string) => {
    navigate(`/results/${id}`);
  };

  const handleArchive = async (id: string) => {
    setActionLoading(id);
    try {
      await archiveWebinar(id);
      await fetchWebinars();
    } catch (err) {
      console.error('Failed to archive webinar:', err);
      alert('Failed to archive. Please try again.');
    } finally {
      setActionLoading(null);
    }
  };

  const handleUnarchive = async (id: string) => {
    setActionLoading(id);
    try {
      await unarchiveWebinar(id);
      await fetchWebinars();
    } catch (err) {
      console.error('Failed to unarchive webinar:', err);
      alert('Failed to unarchive. Please try again.');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setActionLoading(deleteTarget.id);
    try {
      await deleteWebinar(deleteTarget.id);
      setDeleteTarget(null);
      await fetchWebinars();
    } catch (err) {
      console.error('Failed to delete webinar:', err);
      alert('Failed to delete. Please try again.');
    } finally {
      setActionLoading(null);
    }
  };

  const handleShareWebinar = async (webinar: WebinarListItem) => {
    setActionLoading(webinar.id);
    try {
      if (webinar.share_token) {
        // Already shared — just copy link
        const url = `${window.location.origin}/script/${webinar.share_token}`;
        await navigator.clipboard.writeText(url);
        setLinkCopied(webinar.id);
        setTimeout(() => setLinkCopied(null), 2500);
      } else {
        // Generate new share token
        const token = await shareWebinar(webinar.id);
        const url = `${window.location.origin}/script/${token}`;
        await navigator.clipboard.writeText(url);
        setLinkCopied(webinar.id);
        setTimeout(() => setLinkCopied(null), 2500);
        await fetchWebinars();
      }
    } catch (err) {
      console.error('Failed to share webinar:', err);
      alert('Failed to share. Please try again.');
    } finally {
      setActionLoading(null);
    }
  };

  const handleMakeEvergreen = async (webinar: WebinarListItem) => {
    setActionLoading(webinar.id);
    try {
      const slug = await makeEvergreen(webinar.id);
      const url = `${window.location.origin}/webinar/${slug}`;
      await navigator.clipboard.writeText(url);
      setEvergreenCopied(webinar.id);
      setTimeout(() => setEvergreenCopied(null), 2500);
      await fetchWebinars();
    } catch (err) {
      console.error('Failed to make evergreen:', err);
      alert('Failed to save as evergreen. Please try again.');
    } finally {
      setActionLoading(null);
    }
  };

  const handleRemoveEvergreen = async () => {
    if (!removeEvergreenTarget) return;
    setActionLoading(removeEvergreenTarget.id);
    try {
      await removeEvergreen(removeEvergreenTarget.id);
      setRemoveEvergreenTarget(null);
      await fetchWebinars();
    } catch (err) {
      console.error('Failed to remove evergreen:', err);
      alert('Failed to remove evergreen. Please try again.');
    } finally {
      setActionLoading(null);
    }
  };

  const handleCopyEvergreenLink = async (webinar: WebinarListItem) => {
    if (!webinar.evergreen_slug) return;
    const url = `${window.location.origin}/webinar/${webinar.evergreen_slug}`;
    await navigator.clipboard.writeText(url);
    setEvergreenCopied(webinar.id);
    setTimeout(() => setEvergreenCopied(null), 2500);
  };

  const handleRevoke = async () => {
    if (!revokeTarget) return;
    setActionLoading(revokeTarget.id);
    try {
      await revokeShare(revokeTarget.id);
      setRevokeTarget(null);
      await fetchWebinars();
    } catch (err) {
      console.error('Failed to revoke share:', err);
      alert('Failed to revoke. Please try again.');
    } finally {
      setActionLoading(null);
    }
  };

  const formatDate = (iso: string) => {
    return new Date(iso).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const statusBadge = (status: string, errorMessage: string | null) => {
    const base = 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium';
    switch (status) {
      case 'complete':
        return (
          <span className={`${base} bg-green-500/10 text-green-400 border border-green-500/20`}>
            Complete
          </span>
        );
      case 'processing':
        return (
          <span className={`${base} bg-amber-500/10 text-amber-400 border border-amber-500/20`}>
            Processing
          </span>
        );
      case 'error':
        return (
          <span
            className={`${base} bg-red-500/10 text-red-400 border border-red-500/20`}
            title={errorMessage || 'An error occurred'}
          >
            Error
          </span>
        );
      default:
        return (
          <span className={`${base} bg-slate-500/10 text-slate-400 border border-slate-500/20`}>
            {status}
          </span>
        );
    }
  };

  const dateFilterOptions: { value: DateFilter; label: string }[] = [
    { value: 'all', label: 'All Time' },
    { value: '7d', label: '7 Days' },
    { value: '30d', label: '30 Days' },
    { value: '90d', label: '90 Days' },
  ];

  return (
    <div className="min-h-screen p-4 md:p-8">
      <div className="w-full max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-2"
        >
          <h1 className="text-3xl font-bold text-slate-100">Your Webinars</h1>
          <p className="text-slate-400">
            View and manage your generated webinar packages
          </p>
        </motion.div>

        {/* Controls */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1 }}
          className="flex flex-col sm:flex-row gap-3"
        >
          {/* Search */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <input
              type="text"
              placeholder="Search webinars..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-slate-900/80 border border-slate-700/50 rounded-xl text-slate-200 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500/50 transition-colors text-sm"
            />
          </div>

          {/* Sort Toggle */}
          <button
            onClick={() => setSortAsc((prev) => !prev)}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-slate-900/80 border border-slate-700/50 rounded-xl text-slate-300 hover:text-white hover:border-slate-600 transition-colors text-sm"
          >
            <ArrowUpDown className="w-4 h-4" />
            {sortAsc ? 'Oldest' : 'Newest'}
          </button>

          {/* Date Filter */}
          <div className="inline-flex items-center gap-1 p-1 bg-slate-900/80 border border-slate-700/50 rounded-xl">
            <Calendar className="w-4 h-4 text-slate-500 ml-2 mr-1" />
            {dateFilterOptions.map((opt) => (
              <button
                key={opt.value}
                onClick={() => setDateFilter(opt.value)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                  dateFilter === opt.value
                    ? 'bg-primary-600/30 text-white border border-primary-500/30'
                    : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </motion.div>

        {/* Archived toggle */}
        {(archivedCount > 0 || showArchived) && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <button
              onClick={() => setShowArchived((prev) => !prev)}
              className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-slate-200 transition-colors"
            >
              <Archive className="w-4 h-4" />
              {showArchived ? 'Hide archived' : `Show archived (${archivedCount})`}
            </button>
          </motion.div>
        )}

        {/* Error state */}
        {error && (
          <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4 text-red-400 text-sm">
            {error}
          </div>
        )}

        {/* Loading state */}
        {loading && (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-primary-500 animate-spin" />
          </div>
        )}

        {/* Empty state */}
        {!loading && filtered.length === 0 && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-slate-900/50 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-12 text-center"
          >
            <div className="w-16 h-16 rounded-full bg-slate-800 flex items-center justify-center mx-auto mb-6">
              <FolderOpen className="w-8 h-8 text-slate-500" />
            </div>
            <h2 className="text-xl font-semibold text-white mb-2">
              {search ? 'No matching webinars' : 'No webinars yet'}
            </h2>
            <p className="text-slate-400 mb-6">
              {search
                ? 'Try adjusting your search or filters.'
                : 'Create your first webinar package to get started.'}
            </p>
            {!search && (
              <button
                onClick={() => navigate('/question/1')}
                className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-400 hover:to-primary-500 text-white font-medium rounded-xl transition-all shadow-lg shadow-primary-500/25"
              >
                <Plus className="w-4 h-4" />
                Get Started
              </button>
            )}
          </motion.div>
        )}

        {/* Webinar list */}
        {!loading && filtered.length > 0 && (
          <AnimatePresence mode="popLayout">
            <div className="space-y-3">
              {filtered.map((webinar, index) => (
                <motion.div
                  key={webinar.id}
                  layout
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, x: -20, transition: { duration: 0.2 } }}
                  transition={{ delay: index * 0.04 }}
                  className={`group bg-slate-900/50 backdrop-blur-xl border rounded-2xl p-5 transition-colors ${
                    webinar.is_archived
                      ? 'border-slate-700/30 opacity-70'
                      : 'border-slate-700/50 hover:border-primary-500/30'
                  }`}
                >
                  <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-1">
                        <h3 className="text-lg font-semibold text-slate-100 truncate">
                          {webinar.title || 'Untitled Webinar'}
                        </h3>
                        {statusBadge(webinar.status, webinar.error_message)}
                        {webinar.is_evergreen && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                            <Infinity className="w-3 h-3" />
                            Evergreen
                          </span>
                        )}
                        {webinar.launch_status === 'launched' && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-500/10 text-green-400 border border-green-500/20">
                            <span className="relative flex h-1.5 w-1.5">
                              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
                              <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-green-400" />
                            </span>
                            Live
                          </span>
                        )}
                        {webinar.launch_status === 'paused' && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-500/10 text-amber-400 border border-amber-500/20">
                            <Pause className="w-3 h-3" />
                            Paused
                          </span>
                        )}
                        {webinar.launch_status === 'cancelled' && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-500/10 text-red-400 border border-red-500/20">
                            Cancelled
                          </span>
                        )}
                        {webinar.share_token && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-500/10 text-blue-400 border border-blue-500/20">
                            <Link2 className="w-3 h-3" />
                            Shared
                          </span>
                        )}
                        {webinar.is_archived && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-slate-700/50 text-slate-400">
                            Archived
                          </span>
                        )}
                      </div>
                      {webinar.subtitle && (
                        <p className="text-sm text-slate-400 truncate mb-1">
                          {webinar.subtitle}
                        </p>
                      )}
                      <p className="text-xs text-slate-500">
                        Created {formatDate(webinar.created_at)}
                      </p>
                      {webinar.status === 'error' && webinar.error_message && (
                        <p className="text-xs text-red-400/80 mt-1 truncate">
                          {webinar.error_message}
                        </p>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {webinar.status === 'complete' && (
                        <button
                          onClick={() => handleView(webinar.id)}
                          className="inline-flex items-center gap-1.5 px-4 py-2 bg-primary-600/20 hover:bg-primary-600/30 text-primary-400 hover:text-primary-300 border border-primary-500/20 rounded-xl text-sm font-medium transition-colors"
                        >
                          <Eye className="w-4 h-4" />
                          View
                        </button>
                      )}

                      {webinar.status === 'complete' && webinar.launch_status && webinar.launch_status !== 'draft' ? (
                        <button
                          onClick={() =>
                            navigate(
                              `/status/${webinar.id}`
                            )
                          }
                          className="inline-flex items-center gap-1.5 px-3 py-2 text-green-400 hover:text-green-300 hover:bg-green-500/10 rounded-xl text-sm transition-colors"
                          title="View Status"
                        >
                          <Activity className="w-4 h-4" />
                        </button>
                      ) : webinar.status === 'complete' ? (
                        <button
                          onClick={() =>
                            navigate(
                              `/launch/${webinar.id}`
                            )
                          }
                          className="inline-flex items-center gap-1.5 px-3 py-2 text-purple-400 hover:text-purple-300 hover:bg-purple-500/10 rounded-xl text-sm transition-colors"
                          title="Configure Launch"
                        >
                          <Rocket className="w-4 h-4" />
                        </button>
                      ) : null}

                      {webinar.status === 'complete' && (
                        <button
                          onClick={() => handleShareWebinar(webinar)}
                          disabled={actionLoading === webinar.id}
                          className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm transition-colors disabled:opacity-50 ${
                            linkCopied === webinar.id
                              ? 'text-green-400 bg-green-500/10'
                              : webinar.share_token
                                ? 'text-blue-400 hover:text-blue-300 hover:bg-blue-500/10'
                                : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
                          }`}
                          title={
                            linkCopied === webinar.id
                              ? 'Link copied!'
                              : webinar.share_token
                                ? 'Copy share link'
                                : 'Share script'
                          }
                        >
                          {actionLoading === webinar.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : linkCopied === webinar.id ? (
                            <Link2 className="w-4 h-4" />
                          ) : webinar.share_token ? (
                            <Link2 className="w-4 h-4" />
                          ) : (
                            <Share2 className="w-4 h-4" />
                          )}
                        </button>
                      )}

                      {webinar.share_token && (
                        <button
                          onClick={() => setRevokeTarget(webinar)}
                          disabled={actionLoading === webinar.id}
                          className="inline-flex items-center gap-1.5 px-3 py-2 text-slate-400 hover:text-amber-400 hover:bg-amber-500/10 rounded-xl text-sm transition-colors disabled:opacity-50"
                          title="Revoke share link"
                        >
                          <Link2Off className="w-4 h-4" />
                        </button>
                      )}

                      {/* Evergreen actions */}
                      {webinar.status === 'complete' && !webinar.is_evergreen && (
                        <button
                          onClick={() => handleMakeEvergreen(webinar)}
                          disabled={actionLoading === webinar.id}
                          className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm transition-colors disabled:opacity-50 ${
                            evergreenCopied === webinar.id
                              ? 'text-emerald-400 bg-emerald-500/10'
                              : 'text-slate-400 hover:text-emerald-400 hover:bg-emerald-500/10'
                          }`}
                          title={evergreenCopied === webinar.id ? 'Evergreen link copied!' : 'Save as Evergreen'}
                        >
                          {actionLoading === webinar.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Infinity className="w-4 h-4" />
                          )}
                        </button>
                      )}

                      {webinar.is_evergreen && (
                        <>
                          <button
                            onClick={() => handleCopyEvergreenLink(webinar)}
                            className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm transition-colors ${
                              evergreenCopied === webinar.id
                                ? 'text-emerald-400 bg-emerald-500/10'
                                : 'text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10'
                            }`}
                            title={evergreenCopied === webinar.id ? 'Link copied!' : 'Copy evergreen link'}
                          >
                            <Globe className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => setRemoveEvergreenTarget(webinar)}
                            disabled={actionLoading === webinar.id}
                            className="inline-flex items-center gap-1.5 px-3 py-2 text-slate-400 hover:text-amber-400 hover:bg-amber-500/10 rounded-xl text-sm transition-colors disabled:opacity-50"
                            title="Remove evergreen"
                          >
                            <Infinity className="w-4 h-4 line-through" />
                          </button>
                        </>
                      )}

                      {webinar.is_archived ? (
                        <button
                          onClick={() => handleUnarchive(webinar.id)}
                          disabled={actionLoading === webinar.id}
                          className="inline-flex items-center gap-1.5 px-3 py-2 text-slate-400 hover:text-white hover:bg-slate-700/50 rounded-xl text-sm transition-colors disabled:opacity-50"
                          title="Unarchive"
                        >
                          {actionLoading === webinar.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <ArchiveRestore className="w-4 h-4" />
                          )}
                        </button>
                      ) : (
                        <button
                          onClick={() => handleArchive(webinar.id)}
                          disabled={actionLoading === webinar.id}
                          className="inline-flex items-center gap-1.5 px-3 py-2 text-slate-400 hover:text-white hover:bg-slate-700/50 rounded-xl text-sm transition-colors disabled:opacity-50"
                          title="Archive"
                        >
                          {actionLoading === webinar.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Archive className="w-4 h-4" />
                          )}
                        </button>
                      )}

                      <button
                        onClick={() => setDeleteTarget(webinar)}
                        disabled={actionLoading === webinar.id}
                        className="inline-flex items-center gap-1.5 px-3 py-2 text-slate-400 hover:text-red-400 hover:bg-red-500/10 rounded-xl text-sm transition-colors disabled:opacity-50"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </AnimatePresence>
        )}

        {/* Back link */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="pt-4"
        >
          <button
            onClick={() => navigate('/')}
            className="text-sm text-slate-500 hover:text-slate-300 transition-colors"
          >
            &larr; Back to Home
          </button>
        </motion.div>
      </div>

      {/* Delete confirmation modal */}
      <AnimatePresence>
        {deleteTarget && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
            onClick={() => setDeleteTarget(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-md bg-slate-900 border border-slate-700/50 rounded-2xl p-6 shadow-2xl"
            >
              <div className="flex items-start gap-4 mb-6">
                <div className="w-10 h-10 rounded-full bg-red-500/10 flex items-center justify-center flex-shrink-0">
                  <AlertTriangle className="w-5 h-5 text-red-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white mb-1">
                    Delete Webinar
                  </h3>
                  <p className="text-sm text-slate-400">
                    Are you sure you want to delete{' '}
                    <span className="text-slate-200 font-medium">
                      {deleteTarget.title || 'Untitled Webinar'}
                    </span>
                    ? This cannot be undone.
                  </p>
                </div>
                <button
                  onClick={() => setDeleteTarget(null)}
                  className="text-slate-500 hover:text-slate-300 transition-colors flex-shrink-0"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setDeleteTarget(null)}
                  className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-xl text-sm font-medium transition-colors border border-slate-700"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  disabled={actionLoading === deleteTarget.id}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-500 text-white rounded-xl text-sm font-medium transition-colors disabled:opacity-50"
                >
                  {actionLoading === deleteTarget.id ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Trash2 className="w-4 h-4" />
                  )}
                  Delete
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Revoke share confirmation modal */}
      <AnimatePresence>
        {revokeTarget && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
            onClick={() => setRevokeTarget(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-md bg-slate-900 border border-slate-700/50 rounded-2xl p-6 shadow-2xl"
            >
              <div className="flex items-start gap-4 mb-6">
                <div className="w-10 h-10 rounded-full bg-amber-500/10 flex items-center justify-center flex-shrink-0">
                  <Link2Off className="w-5 h-5 text-amber-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white mb-1">
                    Revoke Share Link
                  </h3>
                  <p className="text-sm text-slate-400">
                    This will break any existing links to{' '}
                    <span className="text-slate-200 font-medium">
                      {revokeTarget.title || 'Untitled Webinar'}
                    </span>
                    . Anyone with the link will no longer be able to view the script. Continue?
                  </p>
                </div>
                <button
                  onClick={() => setRevokeTarget(null)}
                  className="text-slate-500 hover:text-slate-300 transition-colors flex-shrink-0"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setRevokeTarget(null)}
                  className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-xl text-sm font-medium transition-colors border border-slate-700"
                >
                  Cancel
                </button>
                <button
                  onClick={handleRevoke}
                  disabled={actionLoading === revokeTarget.id}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-amber-600 hover:bg-amber-500 text-white rounded-xl text-sm font-medium transition-colors disabled:opacity-50"
                >
                  {actionLoading === revokeTarget.id ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Link2Off className="w-4 h-4" />
                  )}
                  Revoke
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Remove evergreen confirmation modal */}
      <AnimatePresence>
        {removeEvergreenTarget && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
            onClick={() => setRemoveEvergreenTarget(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-md bg-slate-900 border border-slate-700/50 rounded-2xl p-6 shadow-2xl"
            >
              <div className="flex items-start gap-4 mb-6">
                <div className="w-10 h-10 rounded-full bg-amber-500/10 flex items-center justify-center flex-shrink-0">
                  <Infinity className="w-5 h-5 text-amber-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white mb-1">
                    Remove Evergreen
                  </h3>
                  <p className="text-sm text-slate-400">
                    This will remove the permanent public link for{' '}
                    <span className="text-slate-200 font-medium">
                      {removeEvergreenTarget.title || 'Untitled Webinar'}
                    </span>
                    . Anyone with the evergreen URL will no longer be able to view this webinar. Continue?
                  </p>
                </div>
                <button
                  onClick={() => setRemoveEvergreenTarget(null)}
                  className="text-slate-500 hover:text-slate-300 transition-colors flex-shrink-0"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setRemoveEvergreenTarget(null)}
                  className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-xl text-sm font-medium transition-colors border border-slate-700"
                >
                  Cancel
                </button>
                <button
                  onClick={handleRemoveEvergreen}
                  disabled={actionLoading === removeEvergreenTarget.id}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-amber-600 hover:bg-amber-500 text-white rounded-xl text-sm font-medium transition-colors disabled:opacity-50"
                >
                  {actionLoading === removeEvergreenTarget.id ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Infinity className="w-4 h-4" />
                  )}
                  Remove
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
